<script lang="ts">
	import classNames from 'classnames';
	import { NewsProfileComponentActions } from '../types';
	import { ModalGraphButton } from './ModalGraphButton';
	import { trackOnwardClicks } from '#lib/features/analytics/utils/trackOnwardClicks/trackOnwardClicks.js';
	import {
		isNewsAppLink,
		markExternalForApp,
	} from '#lib/features/navigation/utils/markExternalForApp/markExternalForApp.js';
	import { preserveGlobalNav } from '#lib/features/navigation/utils/preserveGlobalNav/preserveGlobalNav.js';
	import { ALL_STORIES_ID } from '#lib/features/news/components/NewsCardGrid/constants.js';
	import { NewsMixpanelEvents } from '#lib/features/news/constants/mixpanel-events.js';
	import { BASE_SEARCH_CATEGORIES_MAP } from '#lib/websites/news/constants/categories.js';
	import {
		HENI_TAG_PARAM,
		HENI_TAG_PARAM_VALUE,
	} from '#lib/websites/news/constants/heni-tag.js';
	import { NewsRoutes } from '#lib/websites/news/constants/routes.js';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import { Button } from '$global/components/Button';
	import { Link } from '$global/components/Link';
	import { Markdown } from '$global/components/Markdown';
	import { Txt } from '$global/components/Txt';
	import { getPageData } from '$global/utils/getPageData/getPageData';
	import type { NewsHomePageData } from '$routes/news/types';

	interface Props {
		component: NonNullable<
			NonNullable<NewsHomePageData['profile']>['components']
		>[number];
	}

	let { component }: Props = $props();

	const dataCyPrefix = 'news-profile-components';

	let data = $derived(getPageData<NewsHomePageData>(page.data));
	let artistPage = $derived(data.artistPage);

	// The News artist profile is the other News→commerce crossing called out on
	// the ticket: its CTA `button_link` is CMS free text, so the same button can
	// point at the artist's own page, an Editions release or an external site.
	// Delegating over the button row lets `trackOnwardClick` decide from the
	// resolved href, and it keeps every branch below covered by one listener.
	let onwardClickParams = $derived({
		eventName: NewsMixpanelEvents.OnwardClick,
		sourceElement: 'news-profile-cta',
		fromSection: 'news' as const,
		artist: data.profile?.name,
	});
	let showGlobalNav = $derived(page.data.showGlobalNav ?? true);
	let searchParams = $derived(page.url.searchParams);
	// The reserved HENI profile's buttons can point anywhere on the site, and
	// the app must open those in the device browser rather than its article
	// modal. Only that profile carries the marker: artist/geotag profile
	// buttons link within the news app and keep their in-app behaviour.
	// A HENI button that links back into the news app is not external: it
	// stays in-app and keeps global_nav=0 like any other internal news link.
	let buttonLink = $derived.by(() => {
		const href = component?.button_link;

		if (!href || searchParams.get(HENI_TAG_PARAM) !== HENI_TAG_PARAM_VALUE) {
			return href;
		}

		return isNewsAppLink(href, page.url)
			? preserveGlobalNav(href, page.url)
			: markExternalForApp(href, page.url);
	});
	let showFullBio = $state(false);
	let loading = $state(false);
	let newsArticle = $derived(component?.button_news_link);
	let isNewsArticlePublished = $derived(newsArticle?.status === 'published');
	let show = $derived(
		component?.button_action === NewsProfileComponentActions.LinkNews
			? isNewsArticlePublished
			: true
	);

	const getMenuOffset = () => {
		switch (true) {
			case window.innerWidth < 640:
				return 220;
			case window.innerWidth < 1024:
				return 270;
			default:
				return 320;
		}
	};

	const goToAnchorClick = (anchor: string) => {
		const element = document.getElementById(anchor.replace('#', ''));

		const anchorOffset = element?.getBoundingClientRect?.()?.top;

		if (anchorOffset !== undefined) {
			window.scrollTo({
				behavior: 'smooth',
				top: anchorOffset + window.scrollY - getMenuOffset(),
			});
		}
	};

	const handleAnchorButtonClick = (baseAnchor: string) => async () => {
		const anchor = baseAnchor.replace('#', '');

		const categories = Object.values(BASE_SEARCH_CATEGORIES_MAP).map(
			(category) => category.toLowerCase()
		);

		const isCategoryAnchor = categories.includes(anchor.toLowerCase());

		const isAllStoriesAnchor = anchor.toLowerCase() === ALL_STORIES_ID;

		// `page.url.searchParams` is readonly in SvelteKit 3: edit a copy.
		const params = new URLSearchParams(searchParams.toString());

		if (isAllStoriesAnchor) {
			params.delete('category');

			await goto(`?${params.toString()}`, {
				replace: false,
				reset: false,
			});

			goToAnchorClick(ALL_STORIES_ID);
			return;
		}

		if (isCategoryAnchor) {
			params.set('category', anchor);

			await goto(`?${params.toString()}`, {
				replace: false,
				reset: false,
			});

			goToAnchorClick(ALL_STORIES_ID);
			return;
		}

		goToAnchorClick(anchor);
	};

	let showArtistPageBio = $derived(
		!showGlobalNav &&
			component?.button_link?.endsWith('#artists-about') &&
			artistPage
	);

	// The `#artists-about` button triggers a full page load, so its spinner is
	// only ever seen again when the browser restores this page from the
	// back/forward cache. Clearing it on that restore replaces the old
	// pagehide/beforeunload/onDestroy resets, the last of which wrote state
	// during teardown and threw `state_unsafe_mutation` (Sentry #2922).
	const handlePageShow = (event: PageTransitionEvent) => {
		if (event.persisted) {
			loading = false;
		}
	};
</script>

<svelte:window onpageshow={handlePageShow} />

{#if show}
	<div>
		<div
			class="mb-4 flex min-h-[32px] items-center justify-between"
			use:trackOnwardClicks={onwardClickParams}
		>
			<Txt variant="h5">
				{component?.title}
			</Txt>

			{#if component?.button_action === NewsProfileComponentActions.LinkExternal}
				<Link href={buttonLink} target="_blank" rel="noopener noreferrer">
					<Button dataCy={dataCyPrefix} size="sm" variant="primary">
						{component?.button_label}
					</Button>
				</Link>
			{/if}

			{#if component?.button_action === NewsProfileComponentActions.LinkInternal}
				{#if showArtistPageBio}
					<Button
						dataCy={dataCyPrefix}
						size="sm"
						variant="primary"
						onclick={() => {
							showFullBio = !showFullBio;
						}}
					>
						{showFullBio ? 'Read less' : component?.button_label}
					</Button>
				{:else if component?.button_link?.endsWith('#artists-about')}
					<a data-sveltekit-reload href={component?.button_link}>
						<Button
							dataCy={dataCyPrefix}
							size="sm"
							variant="primary"
							{loading}
							onclick={() => {
								loading = true;
							}}
						>
							{component?.button_label}
						</Button>
					</a>
				{:else}
					<Link href={buttonLink}>
						<Button dataCy={dataCyPrefix} size="sm" variant="primary">
							{component?.button_label}
						</Button>
					</Link>
				{/if}
			{/if}

			{#if component?.button_action === NewsProfileComponentActions.LinkNews}
				<Link href={`${NewsRoutes.Article}/${newsArticle?.slug}`}>
					<Button dataCy={dataCyPrefix} size="sm" variant="primary">
						{component?.button_label}
					</Button>
				</Link>
			{/if}

			{#if component?.button_action === NewsProfileComponentActions.LinkAnchor}
				<Button
					dataCy={dataCyPrefix}
					size="sm"
					variant="primary"
					onclick={handleAnchorButtonClick(component?.button_link || '')}
				>
					{component?.button_label}
				</Button>
			{/if}

			{#if component?.button_action === NewsProfileComponentActions.ModalGraph}
				<ModalGraphButton
					buttonLabel={component?.button_label || ''}
					buttonData={component.button_data}
				/>
			{/if}
		</div>
		<!-- Parsed by the server load (withProfileContentTokensServer), which
		picks the content, or the artist's biography in the app. -->
		<Markdown
			dataCy={dataCyPrefix}
			source={component?.contentTokens ?? []}
			class={classNames({
				'line-clamp-6': showArtistPageBio && !showFullBio,
			})}
		/>
	</div>
{/if}
